import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Mail, MessageCircle, AlertCircle } from '../../icons';
import {
  fetchTeamHr, fetchTeamHrPerson, hourMinText, tenureText,
  type TeamHrListDto, type TeamHrDetailDto,
} from '../../api/hr-team-hr';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import './HrShell.css';

/**
 * 부서원 인사정보 — 팀장 화면 (시안 B10).
 *
 * ★★ **담당자 화면(A3)보다 좁다.** 팀장이 보는 것은 «일을 같이 하는 데 필요한 것» 까지다 —
 *    주민등록번호 · 계좌 · 주소 · 임금은 오지 않고, **개인 휴대전화와 생일도 없다**.
 *    서버가 그 칸을 아예 안 보내므로 화면에서 가리는 것이 아니다(가리기는 부족하다).
 * ★★ **못 보는 것을 화면이 말한다.** 빈칸으로 두면 「없는 사람」 처럼 읽힌다 — 무엇이 왜
 *    여기 없는지 한 줄 적어, 필요하면 인사 담당자에게 요청할 수 있게 한다.
 * ★ 근태 숫자는 «내 근태» 와 **같은 계산**이다 — 두 화면이 다른 숫자를 말하면 안 된다.
 * ★ 볼 수 없는 사람이면 화면을 접는다(팀장도 관리자도 아니면 서버가 403).
 */

/** 담당자에게 남는 것 — 팀장 화면에서 열지 않는다. */
const STAYS_WITH_HR = '주민등록번호 · 계좌 · 주소 · 임금 · 포상 · 징계 · 인사평가';

export function TeamHrPage() {
  const [board, setBoard] = useState<TeamHrListDto | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  const [teamId, setTeamId] = useState<string | null>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TeamHrDetailDto | null>(null);
  const [detailState, setDetailState] = useState<'idle' | 'loading' | 'ok' | 'folded'>('idle');
  const [q, setQ] = useState('');

  const load = useCallback(async (id?: string) => {
    setState('loading');
    try {
      const r = await fetchTeamHr(id);
      setBoard(r);
      setTeamId(r.team.id);
      setState('ok');
      // 첫 사람을 열어 둔다 — 빈 오른쪽은 「없다」 로 읽힌다.
      setPickedId((prev) => (prev && r.members.some((m) => m.userId === prev) ? prev : r.members[0]?.userId ?? null));
    } catch {
      setState('folded');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!pickedId) { setDetail(null); setDetailState('idle'); return; }
    let alive = true;
    setDetailState('loading');
    (async () => {
      try {
        const r = await fetchTeamHrPerson(pickedId);
        if (alive) { setDetail(r); setDetailState('ok'); }
      } catch {
        if (alive) { setDetail(null); setDetailState('folded'); }
      }
    })();
    return () => { alive = false; };
  }, [pickedId]);

  const members = useMemo(() => {
    const list = board?.members ?? [];
    const needle = q.trim();
    return needle ? list.filter((m) => m.name.includes(needle) || (m.position ?? '').includes(needle)) : list;
  }, [board, q]);

  const p = detail?.person;
  const tenure = tenureText(p?.hireDate ?? null);

  return (
    <div className="hrpage hrteam">
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/me/team-hr')}
        scope={scopeFor('/erp/hr/me/team-hr')}
        title="부서원 인사정보"
      />

      {state === 'loading' && <div className="hrpage__note">불러오는 중…</div>}
      {state === 'folded' && (
        <div className="hrpage__note">
          부서원 인사정보는 <b>팀장과 회사 관리자</b>만 볼 수 있습니다.
        </div>
      )}

      {state === 'ok' && board && (
        <div className="hrteam__split">
          <aside className="hrteam__roster" aria-label="팀 명부">
            <div className="hrteam__rosterhead">
              <h2>팀 명부</h2>
              {board.teams.length > 1 ? (
                <select
                  value={teamId ?? ''} aria-label="팀 고르기"
                  onChange={(e) => { setTeamId(e.target.value); void load(e.target.value); }}
                >
                  {board.teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              ) : (
                <span className="hrpage__dim">{board.team.name}</span>
              )}
              <span className="hrpage__pill">{board.members.length}명</span>
            </div>

            <label className="hrteam__find">
              <Search size={14} aria-hidden />
              <input
                value={q} placeholder="이름으로 찾기" aria-label="이름으로 찾기"
                onChange={(e) => setQ(e.target.value)}
              />
            </label>

            {members.length === 0 ? (
              <p className="hrpage__empty">찾는 사람이 없습니다.</p>
            ) : (
              <ul className="hrteam__list">
                {members.map((m) => (
                  <li key={m.userId}>
                    <button
                      type="button"
                      className={`hrteam__person${m.userId === pickedId ? ' is-on' : ''}`}
                      aria-current={m.userId === pickedId ? 'true' : undefined}
                      onClick={() => setPickedId(m.userId)}
                    >
                      <span className="hrteam__nm">{m.name}</span>
                      <span className="hrpage__dim">{m.position ?? '직위 없음'}</span>
                      {m.isLeader && <span className="hrpage__pill hrteam__leader">팀장</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <div className="hrteam__main">
            {detailState === 'loading' && <div className="hrpage__note">불러오는 중…</div>}
            {detailState === 'idle' && <div className="hrpage__note">왼쪽에서 사람을 고르세요.</div>}
            {detailState === 'folded' && <div className="hrpage__note">이 사람의 정보를 볼 수 없습니다.</div>}

            {detailState === 'ok' && detail && p && (
              <>
                <section className="hrpage__panel hrteam__who">
                  <div className="hrteam__whoname">
                    <b>{p.name}</b>
                    {p.nameEn && <span className="hrpage__dim">{p.nameEn}</span>}
                    {p.employmentStatus && <span className="hrpage__pill">{p.employmentStatus === 'ACTIVE' ? '재직' : p.employmentStatus}</span>}
                    {p.isLeader && <span className="hrpage__pill hrteam__leader">팀장</span>}
                  </div>
                  <div className="hrteam__whosub">
                    {[p.divisionName, p.teamName, p.position].filter(Boolean).join(' · ')}
                    {p.employeeNumber && <> · 사번 {p.employeeNumber}</>}
                    {tenure && <> · 근속 {tenure}</>}
                  </div>
                  <div className="hrteam__whoacts">
                    <a className="hrjg__link" href={`mailto:${p.email}`}>
                      <Mail size={14} aria-hidden /> 메일
                    </a>
                    <span className="hrpage__dim"><MessageCircle size={14} aria-hidden /> {p.extension ? `내선 ${p.extension}` : '내선 없음'}</span>
                  </div>
                </section>

                <div className="hrpage__row">
                  <section className="hrpage__panel">
                    <h2 className="hrpage__panelt">신상</h2>
                    <table className="hrpage__tbl hrteam__tbl">
                      <tbody>
                        <tr><td>이름</td><td>{p.name}{p.nameEn ? ` (${p.nameEn})` : ''}</td></tr>
                        <tr><td>소속</td><td>{[p.divisionName, p.teamName].filter(Boolean).join(' · ')}</td></tr>
                        <tr><td>직위</td><td>{p.position ?? <span className="hrpage__dim">없음</span>}</td></tr>
                        <tr>
                          <td>입사일</td>
                          <td>{p.hireDate ?? <span className="hrpage__dim">미설정</span>}{tenure ? ` (근속 ${tenure})` : ''}</td>
                        </tr>
                        <tr>
                          <td>사내 연락</td>
                          <td>{p.extension ? `내선 ${p.extension} · ` : ''}{p.email}</td>
                        </tr>
                        <tr>
                          <td>근무제</td>
                          <td>{p.scheduleName ? `${p.scheduleName} ${p.baseStart} – ${p.baseEnd}` : `${p.baseStart} – ${p.baseEnd}`}</td>
                        </tr>
                      </tbody>
                    </table>
                    {/* ★ 못 보는 것을 빈칸으로 두지 않는다 — 무엇이 왜 없는지 적는다. */}
                    {/* ★ 글은 **한 덩어리**로 둔다 — 굵은 조각을 flex 자식으로 만들면
                        문장이 세 토막으로 갈려 읽히지 않는다(실측으로 잡았다). */}
                    <p className="hrteam__why">
                      <AlertCircle size={14} aria-hidden />
                      <span>
                        {STAYS_WITH_HR}는 <b>팀장에게 오지 않습니다</b> — 필요하면 인사 담당자에게 요청하세요.
                      </span>
                    </p>
                  </section>

                  <section className="hrpage__panel">
                    <h2 className="hrpage__panelt">
                      이 사람의 근태
                      <span className="hrpage__panels">{detail.year}년 {detail.month}월</span>
                    </h2>
                    <table className="hrpage__tbl hrteam__tbl">
                      <tbody>
                        <tr>
                          <td>근무</td>
                          <td>
                            {hourMinText(detail.attendance.creditedMinutes)}
                            <span className="hrpage__dim"> · {detail.attendance.workedDays}일</span>
                          </td>
                        </tr>
                        <tr>
                          <td>연차 잔여</td>
                          <td>
                            {detail.leave.remainDays === null
                              ? <span className="hrpage__dim">아직 산정되지 않음</span>
                              : <>{detail.leave.remainDays}일 <span className="hrpage__dim">· 총 {detail.leave.annualTotal} 사용 {detail.leave.usedDays}</span></>}
                          </td>
                        </tr>
                        <tr>
                          <td>지각</td>
                          <td className={detail.attendance.late > 0 ? 'hrhome__hot' : ''}>{detail.attendance.late}회</td>
                        </tr>
                        <tr>
                          <td>미퇴근</td>
                          <td className={detail.attendance.needsCloseDays > 0 ? 'hrhome__hot' : ''}>
                            {detail.attendance.needsCloseDays}일
                          </td>
                        </tr>
                        <tr>
                          <td>결재 대기</td>
                          <td>
                            {detail.pendingRequests.total}건
                            <span className="hrpage__dim"> · 휴가 {detail.pendingRequests.leave} · 연장 {detail.pendingRequests.overtime}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </section>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
